import { containsOmittedPath } from 'core/helpers/diff-paths';
import { dateTimeFormatterInUserTimezone } from 'core/helpers/formatters';

const parseTime = (timestamp) => {
  const parsed = timestamp ? new Date(timestamp).getTime() : Number.NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const orderOffersForDiff = (offerA, offerB) => {
  const creationA = parseTime(offerA?.initialCreationDateTime || offerA?.updatedDateTime);
  const creationB = parseTime(offerB?.initialCreationDateTime || offerB?.updatedDateTime);
  const aIsOlder =
    creationA === creationB
      ? parseTime(offerA?.updatedDateTime) <= parseTime(offerB?.updatedDateTime)
      : creationA < creationB;
  return aIsOlder ? { oldOffer: offerA, newOffer: offerB } : { oldOffer: offerB, newOffer: offerA };
};

// Path prefixes, suffixes, or substrings to exclude from the diff — add here as needed.
const SKIP_PATH_PREFIXES = ['options.0.', 'options.1.', 'options.2.', 'options.3.'];
const SKIP_PATH_SUFFIXES = ['.amount'];
const SKIP_PATH_CONTAINS = ['replacementCostEstimate.itemized', 'characteristicsData', 'accuracyScore'];
const removeUnwantedKeys = (path) =>
  SKIP_PATH_PREFIXES.some((p) => path.startsWith(p)) ||
  SKIP_PATH_SUFFIXES.some((s) => path.endsWith(s)) ||
  SKIP_PATH_CONTAINS.some((s) => path.includes(s));

export const buildOfferDiffChanges = (diffData) => {
  const result = diffData?.getOfferDifference;
  if (!result?.success || !result.difference) {
    return [];
  }
  let addedChanges;
  let removedChanges;
  try {
    addedChanges = JSON.parse(result.difference.addedChanges ?? '{}');
    removedChanges = JSON.parse(result.difference.removedChanges ?? '{}');
  } catch {
    return [];
  }
  const allKeys = new Set([...Object.keys(addedChanges), ...Object.keys(removedChanges)]);
  return Array.from(allKeys)
    .filter((path) => !removeUnwantedKeys(path) && !containsOmittedPath(path))
    .map((path) => ({
      path,
      previousValue: String(removedChanges[path] ?? 'null'),
      currentValue: String(addedChanges[path] ?? 'null')
    }));
};

const UUID_IN_TEXT = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

// Agents often paste a full offer URL — pull the offer ID (UUID) out of whatever
// was entered; bare IDs and unrecognized text pass through unchanged.
export const extractOfferId = (input) => {
  const trimmed = (input ?? '').trim();
  const match = trimmed.match(UUID_IN_TEXT);
  return match ? match[0] : trimmed;
};

export const dateLabel = (timestamp) =>
  timestamp &&
  dateTimeFormatterInUserTimezone(timestamp, {
    dateOptions: { month: 'short', day: 'numeric' },
    timeOptions: { timeZoneName: 'short' }
  });
